import { Injectable } from '@angular/core';
import { API } from './api';
import { LocalStorageProvider } from './local-storage';
import { BaseProvider } from './base';
import { EventName, Events } from '../utils/events';
import { NumberProvider } from './number';

export interface JobItem {
    name: string;
    message: string;
    current: number;
    total: number;
    addDate: number;
}

/**
 * Job notification file name value that applies to more than
 * just a single upload file
 */
export const MULTI_FILE_NOTIFICATION_NAME = '(multiple files)';

export interface JobNotification {
    message: string;
    success: boolean;
}

export class Job {
    state: string;
    type: JobType;

    /**
     * For most cases the type of this property can be read as JobNotification[].
     * (Currently leaving this as any[] to support a design divergence for the
     * duplicate dataset job)
     */
    notifications: any[];

    items: JobItem[];
}

export class TrackedJob {
    id: string;
    data: any = {};
}

export enum JobType {
    Segmenter = 'segmenter',
    Export = 'export',
    Upload = 'upload',
    IncrementalTrain = 'incrementalTrain',
    Retrain = 'retrain',
    Optimize = 'optimize',
    Freeze = 'freeze',
    Train = 'train',
    DuplicateDataseat = 'duplicateDataset',
    AnomalyHiFi = 'trainAnomalyHiFi',
    UploadTestSet = 'uploadTestSet',
    BrainExport = 'brainExport'
}

export enum JobState {
    READY = 'Ready',
    WAITING = 'Waiting',
    UPLOADING = 'Uploading',
    PROCESSING = 'Processing',
    DONE = 'Done',
    ERROR = 'Error',
    CANCEL_REQUESTED = 'Cancel Requested',
    CANCELING = 'Canceling',
    CANCELED = 'Canceled'
}

export interface JobResponse {
    jobIdentifier: string;
    type: JobType;
    datasetIdentifier: string;
    state: JobState;
    stateDescription?: string;
    dateProcessingStartedSeconds: number;
    current?: number | null;
    total?: number | null;
    errorCode?: string | null;
    errorMessage?: string | null;
    isDismissed?: boolean;

    previousState?: JobState | null;
}

interface JobResponseMap {
    [key: string]: JobResponse;
}

/**
 * Map of job type keys to JobRecord
 */
export interface TypeToJobMap {
    [key: string]: JobResponse;
}

/**
 * Map of datasetIdentifier keys to TypeToJobMaps
 */
export interface DatasetToTypeToJobMap {
    [key: string]: TypeToJobMap;
}

export interface JobIdentifierToJobMap {
    [key: string]: JobResponse;
}

export interface JobsEventData {
    jobs: JobResponse[];
}

export interface JobEventData {
    jobIdentifier: string | null;
    type: JobType;
    datasetIdentifier?: string;
    state: JobState;
    errorCode?: string | null | undefined;
}

export class JobFailedError extends Error {}

@Injectable()
export class JobProvider extends BaseProvider {
    private jobResponses: JobResponseMap = {};
    private datasetJobs: DatasetToTypeToJobMap = {};

    private mapOfPolledJobs = new Map();

    // Kept for backwards compatibility
    private jobs: any = {};

    private trackedJobs: any = {};
    private trackedJobsIndex: any = {};

    constructor(
        public api: API,
        private events: Events,
        private localStorageProvider: LocalStorageProvider,
        private numberProvider: NumberProvider
    ) {
        super();
    }

    public async load(force: boolean = false) {
        return await this.ensureLoaded(() => this.loadJobCache(), force);
    }

    /**
     * Creates a new job and registers it in the job state management system
     */
    public async createUploadJob(payload: any = {}, jobType: JobType = JobType.Upload): Promise<string> {
        const endpoint = `jobs?=${jobType}`;
        const response = await this.api.post(endpoint, payload);

        this.jobs[response.message] = {
            state: 'Processing',
            notifications: []
        };

        return response.message;
    }

    /**
     * Fetches the current status of a job
     */
    public async getJobState(jobIdentifier: string): Promise<Job> {
        const response: any = await this.api.get(`jobs/${jobIdentifier}/state`);

        this.jobs[jobIdentifier] = {
            state: response.state,
            notifications: response.notifications,
            items: response.items,
            type: response.type
        };

        return this.jobs[jobIdentifier];
    }

    /**
     * Fetches a job status from the local cache
     */
    public getJob(jobIdentifier: string): Job {
        return this.jobs[jobIdentifier];
    }

    private ensureJobTypeCacheExists(type: JobType): void {
        if (!this.trackedJobs[type]) {
            this.trackedJobs[type] = [];
        }

        if (!this.trackedJobsIndex[type]) {
            this.trackedJobsIndex[type] = {};
        }
    }

    /**
     * Load tracked jobs from cache
     */
    private async loadJobCache(): Promise<void> {
        const stringifiedCache = await this.localStorageProvider.get('jobCache');
        this.trackedJobs = JSON.parse(stringifiedCache) || {};

        this.buildJobCacheIndex();
    }

    /**
     * Save tracked jobs to cache
     */

    public saveJobCache(): void {
        this.localStorageProvider.set('jobCache', JSON.stringify(this.trackedJobs));
    }

    private buildJobCacheIndex(): void {
        this.trackedJobsIndex = {};

        for (const jobType in this.trackedJobs) {
            this.buildJobCacheIndexForType(jobType);
        }
    }
    private buildJobCacheIndexForType(type: string): void {
        this.trackedJobsIndex[type] = {};

        this.trackedJobs[type].forEach((job, i) => {
            this.trackedJobsIndex[type][job.id] = i;
        });
    }

    public trackJob(id: string, data: any = {}, type: JobType): TrackedJob {
        if (!type) {
            throw new Error('Invalid job type');
        }
        if (!id) {
            throw new Error('Job identifier required');
        }

        const trackedJob = new TrackedJob();

        trackedJob.id = id;
        trackedJob.data = data;

        this.ensureJobTypeCacheExists(type);

        const location = this.trackedJobsIndex[type][id];

        if (Number.isInteger(location)) {
            return this.trackedJobs[type][location];
        }

        console.log(`[JobProvider] Tracking job ${type}:${id}`);

        this.trackedJobs[type].push(trackedJob);
        this.trackedJobsIndex[type][id] = this.trackedJobs[type].length - 1;

        this.saveJobCache();

        return trackedJob;
    }

    public untrackJob(id: string, type: JobType): void {
        this.ensureJobTypeCacheExists(type);

        const location = this.trackedJobsIndex[type][id];
        if (!Number.isInteger(location)) {
            return;
        }

        console.log(`[JobProvider] Untracking job ${type}:${id}`);

        this.trackedJobs[type].splice(location, 1);
        this.buildJobCacheIndexForType(type);

        this.saveJobCache();
    }

    public getTrackedJob(type: JobType, id: string): TrackedJob | undefined {
        if (this.trackedJobsIndex[type] === undefined || this.trackedJobs[type] === undefined) {
            return;
        }

        const trackedJobIndex = this.trackedJobsIndex[type][id];
        if (trackedJobIndex === undefined) {
            return;
        }

        return this.trackedJobs[type][trackedJobIndex];
    }

    // If we don't care about stale jobs (i.e the boost provider, which handles its own freshness tracking),
    // we can use this simplified method just to get the list of current jobs.
    public getAllJobsByType(type: JobType): TrackedJob[] {
        this.ensureJobTypeCacheExists(type);
        return this.trackedJobs[type];
    }

    // Jobs other than brain boosting tasks (like segmentation and upload jobs) need to rely on the job provider
    // to determine whether or not a job is stale (and therefore should not be tracked).
    public async getVerifiedJobsByType(type: JobType): Promise<TrackedJob[]> {
        const jobs = this.getAllJobsByType(type);
        const promises = jobs.map(job => this.checkIfJobIsStale(job, type));
        const verifiedJobs = await Promise.all(promises);

        return verifiedJobs.filter(job => job) as TrackedJob[];
    }

    /**
     * Ensures that a given tracked job is not stale. A cached job is considered stale
     * if a) the job status is Done or b) the job is not found (job has been cleared
     * from the cache server-side)
     */
    private async checkIfJobIsStale(job: TrackedJob, type: JobType): Promise<TrackedJob | null> {
        try {
            const jobObject = await this.getJobState(job.id);

            if (jobObject.state == 'Done' || jobObject.state == 'Error') {
                this.untrackJob(job.id, type);
                return null;
            } else {
                return job;
            }
        } catch (err) {
            this.untrackJob(job.id, type);
            return null;
        }
    }

    public resetInfo(): void {
        this.trackedJobs = {};
        this.trackedJobsIndex = {};
        this.jobs = {};
        super.resetInfo();
    }

    public async pollJobWithPromise(
        jobIdentifier: string,
        startTime: Date = new Date(),
        handleInProgress: ((job: Job) => void) | null = null,
        enableBackoff: boolean = true
    ): Promise<void> {
        return await new Promise<void>((resolve, reject) => {
            this.pollJob(
                jobIdentifier,
                job => {
                    if (job.state == JobState.DONE) {
                        resolve();
                    } else {
                        reject(new JobFailedError('Polled job ${jobIdentifier} ended with an error'));
                    }
                },
                startTime,
                handleInProgress,
                enableBackoff
            );
        });
    }

    /**
     * Polls for the job status of a particular job every n seconds.
     * Polling job is cancelled once the job resolves, regardless of state.
     *
     * @param jobIdentifier the job to be tracked
     * @param handleFinish the callback to be executed on completion of the job (contains reference to job object)
     * @param startTime the time at which the pollJob call was kicked off (used for polling back-off logic)
     * @param handleInProgress the callback to be executed whenever the job updates (contains reference to job object)
     * @param enableBackoff allows us to gradually reduce how frequently we poll for longer jobs
     *
     * @return void
     */
    public pollJob(
        jobIdentifier: string,
        handleFinish: ((job: Job) => void) | null = null,
        startTime: Date = new Date(),
        handleInProgress: ((job: Job) => void) | null = null,
        enableBackoff: boolean = true
    ): void {
        const timeout = setTimeout(async () => {
            await this.updateJobState(jobIdentifier, handleFinish, startTime, handleInProgress);
        }, this.getJobTimeout(startTime, enableBackoff)) as any;

        this.registerPollJob(jobIdentifier, timeout);
    }

    /**
     * Poll a job a single time.
     * Having this as a public method allows us to poll a job outside the usual polling cycle if need be.
     * It takes all of the same parameters as `pollJob()`, except for `enableBackoff`.
     * @return void
     */
    public async updateJobState(
        jobIdentifier: string,
        handleFinish: ((job: Job) => void) | null = null,
        startTime: Date = new Date(),
        handleInProgress: ((job: Job) => void) | null = null
    ): Promise<void> {
        const job = await this.getJobState(jobIdentifier);
        const jobEventData = this.getJobEventDataFromJobState(job, jobIdentifier);

        switch (job.state) {
            case 'Canceled':
            case 'Done':
            case 'Error':
                this.events.publish(EventName.JobFinished, jobEventData);

                if (handleFinish) {
                    handleFinish(job);
                }

                break;
            default:
                if (handleInProgress) {
                    handleInProgress(job);
                }

                this.pollJob(jobIdentifier, handleFinish, startTime, handleInProgress, false);
                break;
        }
    }

    public isJobCurrentlyPolled(jobIdentifier: string): boolean {
        return this.mapOfPolledJobs.has(jobIdentifier);
    }

    public registerPollJob(jobIdentifier: string, timeout: number): void {
        this.mapOfPolledJobs.set(jobIdentifier, timeout);
    }

    public stopPollJob(jobIdentifier: string): void {
        if (this.mapOfPolledJobs.has(jobIdentifier)) {
            const timeout = this.mapOfPolledJobs.get(jobIdentifier);
            this.mapOfPolledJobs.delete(jobIdentifier);
            clearTimeout(timeout);
        }
    }

    public getJobTimeout(start: Date, enableBackoff: boolean): number {
        const now = new Date();

        // All times are in milliseconds.
        if (enableBackoff) {
            if (now.getTime() - start.getTime() >= 600000) {
                // After ten minutes, poll every 30 seconds
                return 30000;
            } else if (now.getTime() - start.getTime() >= 60000) {
                // After one minute, poll every 10 seconds
                return 10000;
            } else {
                // Default, poll every 2 seconds
                return 2000;
            }
        } else {
            // If we've disabled backoff, just poll every 2 seconds no matter what.
            return 2000;
        }
    }

    /**
     * Query the server for currently running and undismissed jobs for the user or organization
     * and update the dataset jobs cache with the results. Return an array of updated jobs
     */
    public async loadJobs(jobTypes: JobType[]): Promise<JobResponse[]> {
        const rawJobs = await this.getJobsFromServer(jobTypes);
        const jobs = rawJobs.map(job => this.getJobWithPreviousState(job));

        // Get affected datasets
        const datasets = {};
        for (const job of jobs) {
            if (job.datasetIdentifier) {
                datasets[job.datasetIdentifier] = true;
            }
        }

        // Clear affected datasets
        for (const datasetIdentifier in datasets) {
            delete this.datasetJobs[datasetIdentifier];
        }

        for (const job of jobs) {
            this.updateJobCollections(job);
        }

        return jobs;
    }

    /**
     * Query the server for currently running and undismissed jobs for the current dataset
     * and update the dataset jobs cache with the results. Return a map of job types to jobs
     * for the dataset
     */
    public async loadDatasetJobs(datasetIdentifier: string, jobTypes: JobType[]): Promise<TypeToJobMap> {
        const rawJobs = await this.getDatasetJobsFromServer(datasetIdentifier, jobTypes);
        const jobs = rawJobs.map(job => this.getJobWithPreviousState(job));

        delete this.datasetJobs[datasetIdentifier];

        for (const job of jobs) {
            this.updateJobCollections(job);
        }

        return this.getUndismissedDatasetJobs(datasetIdentifier);
    }

    /**
     * Query the server for the specified job and update the local job collections
     */
    public async loadJob(jobIdentifier: string): Promise<JobResponse> {
        const rawJob = await this.getJobFromServer(jobIdentifier);
        const job = this.getJobWithPreviousState(rawJob);
        this.updateJobCollections(job);
        return this.jobResponses[jobIdentifier];
    }

    public unloadJob(jobIdentifier: string): void {
        const job = this.jobResponses[jobIdentifier];

        if (job) {
            if (job.datasetIdentifier) {
                const datasetJobs = this.datasetJobs[job.datasetIdentifier];

                if (datasetJobs) {
                    const datasetJob = datasetJobs[job.type];

                    if (datasetJob) {
                        delete datasetJobs[job.type];
                    }
                }
            }

            delete this.jobResponses[jobIdentifier];

            // Trigger a special frontend (nav sidebar) refresh.
            // Passing in a list of job responses allows us to reuse that component's polling logic.
            // ES2015 doesn't support Object.values(), so compiling the list is more verbose.
            const remainingJobResponses: JobResponse[] = [];
            for (const jobKey in this.jobResponses) {
                remainingJobResponses.push(this.jobResponses[jobKey]);
            }

            const jobsEventData: JobsEventData = {
                jobs: remainingJobResponses
            };

            this.events.publish(EventName.JobDismissed, jobsEventData);
        }
    }

    /**
     * Attach any previous state value to the specified JobResponse
     */
    private getJobWithPreviousState(rawJob: JobResponse) {
        const previousJob = this.jobResponses[rawJob.jobIdentifier];
        const previousState = previousJob ? previousJob.state : null;
        const job = { ...rawJob, previousState };
        return job;
    }

    /**
     * Query the server for the current running or undismissed jobs for the user/organization
     */
    public async getJobsFromServer(jobTypes: JobType[]): Promise<JobResponse[]> {
        const response = await this.api.get(`jobs?limit=100&jobTypes=${jobTypes.join(',')}&includeDismissed=false`);
        return response.jobs;
    }

    /**
     * Query the server for the current running or undismissed jobs for the specified dataset and job types
     */
    public async getDatasetJobsFromServer(datasetIdentifier: string, jobTypes: JobType[]): Promise<JobResponse[]> {
        return this.debounce(
            `getDatasetJobsFromServer?datasetIdentifier=${datasetIdentifier}`,
            async () => {
                const response = await this.api.get(
                    `jobs?datasetIdentifier=${datasetIdentifier}&limit=100&jobTypes=${jobTypes.join(
                        ','
                    )}&includeDismissed=false`
                );
                return response.jobs;
            },
            1000
        );
    }

    /**
     * Query the server for the job with the specified jobIdentifier
     */
    private async getJobFromServer(jobIdentifier: string): Promise<JobResponse> {
        const response = await this.api.get(`jobs/${jobIdentifier}`);
        return response.job;
    }

    /**
     * Add the specified job to the job collections in memory
     */
    private updateJobCollections(job: JobResponse): void {
        this.jobResponses[job.jobIdentifier] = job;

        if (job.datasetIdentifier && !job.isDismissed) {
            const existingJob = this.getDatasetJob(job.datasetIdentifier, job.type);

            if (
                !existingJob ||
                existingJob.jobIdentifier == job.jobIdentifier ||
                existingJob.dateProcessingStartedSeconds < job.dateProcessingStartedSeconds
            ) {
                this.setJob(job.datasetIdentifier, job.type, job);
            }
        }
    }

    /**
     * Return the specified job from the local job collection
     */
    public getJobResponse(jobIdentifier: string): JobResponse | null {
        if (!this.jobResponses[jobIdentifier]) {
            console.error(`Could not find job ${jobIdentifier} in the local job collection`);
        }

        return this.jobResponses[jobIdentifier];
    }

    public getJobEventData(job: JobResponse): JobEventData {
        return {
            jobIdentifier: job.jobIdentifier,
            type: job.type,
            datasetIdentifier: job.datasetIdentifier,
            state: job.state,
            errorCode: job.errorCode
        };
    }

    public getJobEventDataFromJobState(job: Job, jobIdentifier: string): JobEventData {
        return {
            jobIdentifier,
            type: job.type,
            state: job.state as JobState
        };
    }

    /**
     * Return true if any of the brain-boosting jobs for the specified dataset is in a running state
     */
    public isJobRunningForDataset(datasetIdentifier: string, jobTypes: JobType[]): boolean {
        if (!datasetIdentifier) {
            return false;
        }

        const typeToJobMap = this.datasetJobs[datasetIdentifier];

        if (!typeToJobMap) {
            return false;
        }

        for (const jobType of jobTypes) {
            const job = typeToJobMap[jobType];

            if (job && !this.isJobDone(job.state)) {
                return true;
            }
        }

        return false;
    }

    public isJobDone(currentState: string): boolean {
        return currentState == JobState.DONE || currentState == JobState.ERROR || currentState == JobState.CANCELED;
    }

    /**
     * Dismiss the specified job. For jobs such as boosting job types
     * this will persist the dismissal state on the server to indicate
     * that we no longer want to display the results of the job to the
     * user
     */
    public async dismiss(jobIdentifier: string): Promise<void> {
        await this.api.post(`jobs/${jobIdentifier}/dismiss`);
        this.unloadJob(jobIdentifier);
    }

    /**
     * Update the job cache with the specified job for the specified dataset and job type
     */
    private setJob(datasetIdentifier: string, jobType: JobType, job: JobResponse): void {
        if (!this.datasetJobs[datasetIdentifier]) {
            this.datasetJobs[datasetIdentifier] = {};
        }

        this.datasetJobs[datasetIdentifier][jobType] = job;
    }

    /**
     * Return the running or undismissed job for the specified dataset and job type.
     * If no job of the specified jobType is present, return null
     */
    public getDatasetJob(datasetIdentifier: string, jobType: string): JobResponse | null {
        const typeToJobMap = this.getUndismissedDatasetJobs(datasetIdentifier);
        return typeToJobMap[jobType] || null;
    }

    public getDatasetFreezeJob(datasetIdentifier: string): JobResponse | null {
        return this.getDatasetJob(datasetIdentifier, JobType.Freeze);
    }

    public async dismissDatasetFreezeJob(datasetIdentifier: string): Promise<void> {
        const job = this.getDatasetFreezeJob(datasetIdentifier);

        if (job) {
            await this.dismiss(job.jobIdentifier);
        }
    }

    public getDatasetTrainJob(datasetIdentifier: string): JobResponse | null {
        return this.getDatasetJob(datasetIdentifier, JobType.Train);
    }

    public async dismissDatasetTrainJob(datasetIdentifier: string): Promise<void> {
        const job = this.getDatasetTrainJob(datasetIdentifier);

        if (job) {
            await this.dismiss(job.jobIdentifier);
        }
    }

    public getDatasetOptimizeJob(datasetIdentifier: string): JobResponse | null {
        return this.getDatasetJob(datasetIdentifier, JobType.Optimize);
    }

    public async dismissDatasetOptimizeJob(datasetIdentifier: string): Promise<void> {
        const job = this.getDatasetOptimizeJob(datasetIdentifier);

        if (job) {
            await this.dismiss(job.jobIdentifier);
        }
    }

    public getDatasetHiFiJob(datasetIdentifier: string): JobResponse | null {
        return this.getDatasetJob(datasetIdentifier, JobType.AnomalyHiFi);
    }

    public async dismissDatasetHiFiJob(datasetIdentifier: string): Promise<void> {
        const job = this.getDatasetHiFiJob(datasetIdentifier);

        if (job) {
            await this.dismiss(job.jobIdentifier);
        }
    }

    public getSanitizedJobProgressPercentage(job: JobResponse | null): number {
        if (!job) {
            return 0;
        }

        const percentage = this.getProgressPercentage(job);

        if (!percentage) {
            return 0;
        }

        const result = this.numberProvider.round(percentage, 0);
        if (!result) {
            return 0;
        }

        return result;
    }

    /**
     * Return a map of job types to running or undismissed jobs for the specified dataset
     */
    public getUndismissedDatasetJobs(datasetIdentifier: string): TypeToJobMap {
        if (this.datasetJobs[datasetIdentifier]) {
            return this.datasetJobs[datasetIdentifier];
        }

        return {};
    }

    /**
     * Return the percent complete for the specified job
     */
    public getProgressPercentage(job: JobResponse): number {
        const current = job.current || 0;
        const total = job.total ? job.total : 1;
        const rawPercentage = (100 * current) / total;
        return Math.round(rawPercentage * 100) / 100;
    }
}



// WEBPACK FOOTER //
// ./src/providers/job.ts